import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Disables right-click context menu and drag-and-drop on every <img>/<video>
 * for visitors, while leaving the admin user fully unrestricted (they need to
 * save covers, share files, etc.).
 *
 * Also flips a `data-protect-images` flag on <html> so CSS can disable
 * user-select / -webkit-touch-callout (long-press menu on iOS Safari).
 *
 * NOTE: this is a *visitor-level deterrent*, not real protection. Determined
 * users can always screenshot, inspect, or open Network tab. Server-side
 * watermarking is the only true protection — out of scope here.
 */
export function useImageProtection() {
  const { user } = useAuth();
  const isAdmin = !!user && user !== false;

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (isAdmin) {
      document.documentElement.removeAttribute("data-protect-images");
      return undefined;
    }

    document.documentElement.setAttribute("data-protect-images", "true");

    const isMedia = (el) => {
      if (!el || !el.tagName) return false;
      const tag = el.tagName.toLowerCase();
      return tag === "img" || tag === "video" || tag === "picture" || tag === "source";
    };

    /** True when `el` (or any of its first 5 ancestors) contains an image —
     *  i.e. the click visually lands on a photograph, even if the actual event
     *  target is a gradient overlay or content layer sitting on top. */
    const isOverImage = (el) => {
      let cur = el;
      let depth = 0;
      while (cur && cur !== document.body && depth < 6) {
        if (cur.querySelector && cur.querySelector("img, video, picture")) {
          return true;
        }
        cur = cur.parentElement;
        depth += 1;
      }
      return false;
    };

    const onContextMenu = (e) => {
      if (isMedia(e.target) || isOverImage(e.target)) e.preventDefault();
    };
    const onDragStart = (e) => {
      if (isMedia(e.target) || isOverImage(e.target)) e.preventDefault();
    };

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("dragstart", onDragStart);

    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("dragstart", onDragStart);
      document.documentElement.removeAttribute("data-protect-images");
    };
  }, [isAdmin]);
}
